import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  createEvent,
  deleteEvent,
  getEvents,
  getGifts,
  getPlans,
  patchEvent,
  patchGift,
  patchPlan,
  type AdminEvent,
  type AdminGift,
  type AdminPlan,
  type PlanEntitlements,
  type PlanPatch,
} from '../api';
import { useApi } from '../useApi';

/** "2026-06-10T18:30:00.000Z" -> local "2026-06-10T21:30" for <input type=datetime-local>. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function useAction() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (id: string, fn: () => Promise<unknown>, then: () => void) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      then();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusyId(null);
    }
  };
  return { busyId, error, run };
}

const initial = (s: string) => (s.trim().charAt(0) || '?').toUpperCase();

// ============================ plans ============================

function PlanCard({ plan, busy, onSave }: { plan: AdminPlan; busy: boolean; onSave: (draft: PlanPatch) => void }) {
  const free = plan.isFree;
  const [name, setName] = useState(plan.name);
  const [priceTon, setPriceTon] = useState(plan.priceTon);
  const [priceUsd, setPriceUsd] = useState(String(plan.priceUsd));
  const [priceStars, setPriceStars] = useState(String(plan.priceStars));
  const [priceTonYearly, setPriceTonYearly] = useState(plan.priceTonYearly ?? '');
  const [priceUsdYearly, setPriceUsdYearly] = useState(plan.priceUsdYearly != null ? String(plan.priceUsdYearly) : '');
  const [priceStarsYearly, setPriceStarsYearly] = useState(plan.priceStarsYearly != null ? String(plan.priceStarsYearly) : '');
  const [icon, setIcon] = useState(plan.icon ?? '');
  const [isFeatured, setIsFeatured] = useState(plan.isFeatured);
  const [perks, setPerks] = useState(plan.perks.join('\n'));
  const perkLines = perks.split('\n').map((p) => p.trim()).filter(Boolean);

  const ent = plan.entitlements ?? {};
  const [unlimitedLikes, setUnlimitedLikes] = useState(ent.unlimitedLikes ?? false);
  const [likesPerDay, setLikesPerDay] = useState(String(ent.likesPerDay ?? 30));
  const [sparksPerDay, setSparksPerDay] = useState(String(ent.sparksPerDay ?? 0));
  const [boostsPerWeek, setBoostsPerWeek] = useState(String(ent.boostsPerWeek ?? 0));
  const [canRewind, setCanRewind] = useState(ent.canRewind ?? false);
  const [seeLikers, setSeeLikers] = useState(ent.seeLikers ?? false);
  const [messageBeforeMatch, setMessageBeforeMatch] = useState(ent.messageBeforeMatch ?? false);
  const [mapReveal, setMapReveal] = useState(ent.mapReveal ?? false);
  const [passport, setPassport] = useState(ent.passport ?? false);

  const save = () => {
    const entitlements: PlanEntitlements = {
      unlimitedLikes,
      likesPerDay: Math.max(0, Math.floor(Number(likesPerDay) || 0)),
      sparksPerDay: Math.max(0, Math.floor(Number(sparksPerDay) || 0)),
      boostsPerWeek: Math.max(0, Math.floor(Number(boostsPerWeek) || 0)),
      canRewind,
      seeLikers,
      messageBeforeMatch,
      mapReveal,
      passport,
    };
    const draft: PlanPatch = {
      name: name.trim(),
      isFeatured,
      icon: icon.trim() === '' ? null : icon.trim(),
      perks: perkLines.slice(0, 12),
      entitlements,
    };
    if (!free) {
      draft.priceTon = priceTon.trim();
      draft.priceUsd = Number(priceUsd);
      draft.priceStars = Number(priceStars);
      draft.priceTonYearly = priceTonYearly.trim() === '' ? null : priceTonYearly.trim();
      draft.priceUsdYearly = priceUsdYearly.trim() === '' ? null : Number(priceUsdYearly);
      draft.priceStarsYearly = priceStarsYearly.trim() === '' ? null : Number(priceStarsYearly);
    }
    onSave(draft);
  };

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span className={`avatar ${free ? '' : plan.id}`} style={{ borderRadius: 12 }}>{initial(name || plan.id)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: '1.05rem' }}>{name || plan.id}</b>
            <span className="mono muted">{plan.id}</span>
            <span className="badge">{plan.periodMonths}m</span>
            {free && <span className="badge">default tier</span>}
            {isFeatured && <span className="badge badge-ok">featured</span>}
          </div>
        </div>
        <div className="row-actions" style={{ alignItems: 'center' }}>
          <label className="check">
            <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
            Featured
          </label>
          <button className="btn btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="form-grid">
        <div className="field">
          <label>Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>Icon key</label>
          <input className="input" value={icon} placeholder="zap / activity / infinity / flame" onChange={(e) => setIcon(e.target.value)} />
        </div>
      </div>

      {free ? (
        <p className="hint" style={{ marginTop: 14 }}>Default tier — not purchasable. Set its free daily limits below.</p>
      ) : (
        <>
          <div className="field-label" style={{ marginTop: 18 }}>Pricing · monthly</div>
          <div className="form-grid" style={{ marginTop: 8 }}>
            <div className="field"><label>TON</label><input className="input" value={priceTon} onChange={(e) => setPriceTon(e.target.value)} /></div>
            <div className="field"><label>USD</label><input className="input" value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)} /></div>
            <div className="field"><label>Stars</label><input className="input" value={priceStars} onChange={(e) => setPriceStars(e.target.value)} /></div>
          </div>
          <div className="field-label" style={{ marginTop: 14 }}>
            Pricing · yearly <span className="hint" style={{ textTransform: 'none', letterSpacing: 0 }}>— blank = no yearly option</span>
          </div>
          <div className="form-grid" style={{ marginTop: 8 }}>
            <div className="field"><label>TON / yr</label><input className="input" value={priceTonYearly} placeholder="—" onChange={(e) => setPriceTonYearly(e.target.value)} /></div>
            <div className="field"><label>USD / yr</label><input className="input" value={priceUsdYearly} placeholder="—" onChange={(e) => setPriceUsdYearly(e.target.value)} /></div>
            <div className="field"><label>Stars / yr</label><input className="input" value={priceStarsYearly} placeholder="—" onChange={(e) => setPriceStarsYearly(e.target.value)} /></div>
          </div>
        </>
      )}

      <div className="field-label" style={{ marginTop: 18 }}>Entitlements <span className="hint" style={{ textTransform: 'none', letterSpacing: 0 }}>— enforced limits</span></div>
      <div className="form-grid" style={{ marginTop: 8 }}>
        {free && (
          <div className="field"><label>Likes / day</label><input className="input" value={likesPerDay} onChange={(e) => setLikesPerDay(e.target.value)} /></div>
        )}
        <div className="field"><label>Sparks / day</label><input className="input" value={sparksPerDay} onChange={(e) => setSparksPerDay(e.target.value)} /></div>
        <div className="field"><label>Boosts / week</label><input className="input" value={boostsPerWeek} onChange={(e) => setBoostsPerWeek(e.target.value)} /></div>
      </div>
      <div className="checks-row" style={{ marginTop: 14 }}>
        {!free && (
          <label className="check">
            <input type="checkbox" checked={unlimitedLikes} onChange={(e) => setUnlimitedLikes(e.target.checked)} />
            Unlimited likes
          </label>
        )}
        <label className="check"><input type="checkbox" checked={canRewind} onChange={(e) => setCanRewind(e.target.checked)} /> Rewind</label>
        <label className="check"><input type="checkbox" checked={seeLikers} onChange={(e) => setSeeLikers(e.target.checked)} /> See likers</label>
        <label className="check"><input type="checkbox" checked={messageBeforeMatch} onChange={(e) => setMessageBeforeMatch(e.target.checked)} /> Message before match</label>
        <label className="check"><input type="checkbox" checked={mapReveal} onChange={(e) => setMapReveal(e.target.checked)} /> Map reveal (see + open nearby)</label>
        <label className="check"><input type="checkbox" checked={passport} onChange={(e) => setPassport(e.target.checked)} /> Passport (worldwide + change location)</label>
      </div>

      <div className="field" style={{ marginTop: 18 }}>
        <label>Perks · one per line ({perkLines.length})</label>
        <textarea
          className="input"
          rows={Math.min(8, Math.max(3, perkLines.length + 1))}
          value={perks}
          placeholder={'perkUnlimited\nMessage before you match\n…or any custom line'}
          onChange={(e) => setPerks(e.target.value)}
        />
        <span className="hint">
          Display only; limits are set above. Reuse a key like <code>perkUnlimited</code> or <code>perkMessageFirst</code> to
          localize it, or type free text. Max 12 lines, 80 chars each.
        </span>
      </div>
    </div>
  );
}

function PlansSection() {
  const { data, error, loading, reload } = useApi(getPlans);
  const { busyId, error: actionError, run } = useAction();
  const rows = data?.data ?? [];

  return (
    <>
      <div className="section-head">
        <h2>Plans<span className="dot">.</span></h2>
        {data && <span className="muted">{rows.length} plans</span>}
      </div>
      {actionError && <p className="error-line">{actionError}</p>}
      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : loading && !data ? (
        <div className="card"><div className="empty">Loading…</div></div>
      ) : rows.length === 0 ? (
        <div className="card"><div className="empty">No plans seeded.</div></div>
      ) : (
        rows.map((p) => (
          <PlanCard key={p.id} plan={p} busy={busyId === p.id} onSave={(draft) => void run(p.id, () => patchPlan(p.id, draft), reload)} />
        ))
      )}
    </>
  );
}

// ============================ gifts ============================

function GiftCard({ gift, busy, onSave }: { gift: AdminGift; busy: boolean; onSave: (d: { name: string; priceTon: string; priceStars: number }) => void }) {
  const [name, setName] = useState(gift.name);
  const [priceTon, setPriceTon] = useState(gift.priceTon);
  const [priceStars, setPriceStars] = useState(String(gift.priceStars));

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="avatar" style={{ borderRadius: 12 }}>{initial(name || gift.id)}</span>
          <b>{name || gift.id}</b>
        </div>
        <span className="mono muted">{gift.id}</span>
      </div>
      <div className="form-grid">
        <div className="field"><label>Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>TON</label><input className="input" value={priceTon} onChange={(e) => setPriceTon(e.target.value)} /></div>
        <div className="field"><label>Stars</label><input className="input" value={priceStars} onChange={(e) => setPriceStars(e.target.value)} /></div>
      </div>
      <div style={{ marginTop: 14 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => onSave({ name: name.trim(), priceTon: priceTon.trim(), priceStars: Number(priceStars) })}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function GiftsSection() {
  const { data, error, loading, reload } = useApi(getGifts);
  const { busyId, error: actionError, run } = useAction();
  const rows = data?.data ?? [];

  return (
    <>
      <div className="section-head">
        <h2>Gift shop<span className="dot">.</span></h2>
        {data && <span className="muted">{rows.length} gifts</span>}
      </div>
      {actionError && <p className="error-line">{actionError}</p>}
      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : loading && !data ? (
        <div className="card"><div className="empty">Loading…</div></div>
      ) : rows.length === 0 ? (
        <div className="card"><div className="empty">No gifts seeded.</div></div>
      ) : (
        <div className="card-grid">
          {rows.map((g) => (
            <GiftCard key={g.id} gift={g} busy={busyId === g.id} onSave={(draft) => void run(g.id, () => patchGift(g.id, draft), reload)} />
          ))}
        </div>
      )}
    </>
  );
}

// ============================ events ============================

function EventCard({
  event,
  busy,
  onSave,
  onDelete,
}: {
  event: AdminEvent;
  busy: boolean;
  onSave: (d: { title: string; venue: string; startsAt: string; depositTon: string | null }) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(event.title);
  const [venue, setVenue] = useState(event.venue);
  const [startsAt, setStartsAt] = useState(toLocalInput(event.startsAt));
  const [deposit, setDeposit] = useState(event.depositTon ?? '');

  return (
    <div className="card">
      <div className="card-head">
        <b>{title || 'Untitled event'}</b>
        <span className="badge">{event.goingCount} going</span>
      </div>
      <div className="form-grid">
        <div className="field"><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="field"><label>Venue</label><input className="input" value={venue} onChange={(e) => setVenue(e.target.value)} /></div>
        <div className="field"><label>Starts</label><input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} /></div>
        <div className="field"><label>Deposit TON</label><input className="input" placeholder="free" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
      </div>
      <div className="row-actions" style={{ marginTop: 14 }}>
        <button
          className="btn btn-primary"
          disabled={busy || !startsAt}
          onClick={() => onSave({ title: title.trim(), venue: venue.trim(), startsAt: new Date(startsAt).toISOString(), depositTon: deposit.trim() === '' ? null : deposit.trim() })}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-danger" disabled={busy} onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

function EventsSection() {
  const { data, error, loading, reload } = useApi(getEvents);
  const { busyId, error: actionError, run } = useAction();
  const [title, setTitle] = useState('');
  const [venue, setVenue] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [deposit, setDeposit] = useState('');
  const rows = data?.data ?? [];

  const create = () =>
    void run(
      'new',
      () =>
        createEvent({
          title: title.trim(),
          venue: venue.trim(),
          startsAt: new Date(startsAt).toISOString(),
          ...(deposit.trim() !== '' ? { depositTon: deposit.trim() } : {}),
        }),
      () => {
        setTitle('');
        setVenue('');
        setStartsAt('');
        setDeposit('');
        reload();
      },
    );

  return (
    <>
      <div className="section-head">
        <h2>Events<span className="dot">.</span></h2>
        {data && <span className="muted">{rows.length} events</span>}
      </div>
      {actionError && <p className="error-line">{actionError}</p>}

      <div className="card">
        <div className="card-head"><span className="card-title">New event</span></div>
        <div className="form-grid">
          <div className="field"><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="field"><label>Venue</label><input className="input" value={venue} onChange={(e) => setVenue(e.target.value)} /></div>
          <div className="field"><label>Starts</label><input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} /></div>
          <div className="field"><label>Deposit TON</label><input className="input" placeholder="blank = free" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" disabled={busyId === 'new' || !title.trim() || !venue.trim() || !startsAt} onClick={create}>
            {busyId === 'new' ? 'Adding…' : 'Add event'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : loading && !data ? (
        <div className="card"><div className="empty">Loading…</div></div>
      ) : rows.length === 0 ? (
        <div className="card"><div className="empty">No events yet — create one above.</div></div>
      ) : (
        <div className="card-grid" style={{ marginTop: 16 }}>
          {rows.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              busy={busyId === e.id}
              onSave={(draft) => void run(e.id, () => patchEvent(e.id, draft), reload)}
              onDelete={() => {
                if (!window.confirm(`Delete event "${e.title}"? RSVPs are removed too.`)) return;
                void run(e.id, () => deleteEvent(e.id), reload);
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ============================ page (route-aware) ============================

type Section = 'plans' | 'gifts' | 'events';

export function Catalog() {
  const { pathname } = useLocation();
  const section: Section = pathname.includes('gift') ? 'gifts' : pathname.includes('event') ? 'events' : 'plans';

  // Each section renders its own heading (route-aware: /plans · /gifts · /events).
  return (
    <div className="admin-wrap">
      {section === 'plans' && <PlansSection />}
      {section === 'gifts' && <GiftsSection />}
      {section === 'events' && <EventsSection />}
    </div>
  );
}
